import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingUpdateDto,
	SmartField_Api_Contracts_Mapping_TowerMappingDto,
	SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
} from "../../api";
import {
	chamberMappingCreateChamberMapping,
	chamberMappingDeleteChamberMapping,
	chamberMappingGetChamberMappings,
	chamberMappingUpdateChamberMapping,
	towerMappingCreateTowerMapping,
	towerMappingDeleteTowerMapping,
	towerMappingGetTowerMappings,
	towerMappingUpdateTowerMapping,
} from "../../api";
import { Modal } from "../../components/ui/Modal";
import { ChamberConfigModal } from "./ChamberConfigModal";
import { TowerGroupConfigModal } from "./TowerGroupConfigModal";
import { TOWER_NAMES } from "./sensorConstants";

type Props = {
	siteId: number;
	year: number;
	canEdit: boolean;
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onBulkApplyChamberHeights: (
		elId: number,
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => Promise<void>;
	onBulkApplyTowerHeights: (
		sensorIds: number[],
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => Promise<void>;
	isBulkApplying: boolean;
};

type EditingChamber = {
	id?: number;
	year: string;
	chamberNumber: string;
	trialId: string;
	plotNumber: string;
};
type EditingTower = {
	id?: number;
	year: string;
	towerName: string;
	trialId: string;
	blockId: string;
};

function parseStrictInt(value: string): number {
	return /^-?\d+$/.test(value.trim()) ? Number(value) : NaN;
}

function emptyEditingChamber(year?: string): EditingChamber {
	return {
		year: year ?? String(new Date().getFullYear()),
		chamberNumber: "",
		trialId: "",
		plotNumber: "",
	};
}

function emptyEditingTower(year?: string): EditingTower {
	return {
		year: year ?? String(new Date().getFullYear()),
		towerName: "",
		trialId: "",
		blockId: "",
	};
}

function SectionToggle({
	label,
	open,
	onToggle,
	count,
}: {
	label: string;
	open: boolean;
	onToggle: () => void;
	count: number;
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className="flex items-center gap-1.5 w-full text-left"
		>
			<svg
				aria-hidden="true"
				className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${open ? "rotate-90" : ""}`}
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M9 5l7 7-7 7"
				/>
			</svg>
			<span className="text-sm font-semibold text-gray-700">{label}</span>
			{!open && (
				<span className="ml-auto text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">
					{count}
				</span>
			)}
		</button>
	);
}

function EditIcon() {
	return (
		<svg
			aria-hidden="true"
			className="w-3 h-3"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
			/>
		</svg>
	);
}

function MappingEditModal<T extends { id?: number; year: string }>({
	open,
	onClose,
	title,
	form,
	fields,
	onChange,
	onSave,
	onDelete,
	isSaving,
	isDeleting,
	canEdit,
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	form: T | null;
	fields: MappingField<T>[];
	onChange: (key: keyof T & string, value: string) => void;
	onSave: () => void;
	onDelete: () => void;
	isSaving: boolean;
	isDeleting: boolean;
	canEdit: boolean;
}) {
	if (!form) {
		return null;
	}
	return (
		<Modal open={open} onClose={onClose} size="md">
			<div className="p-5 space-y-4">
				<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
				<div className="space-y-3">
					{fields.map((f) => {
						const inputId = `mapping-field-${f.key}`;
						return (
							<div key={f.key}>
								<label
									htmlFor={inputId}
									className="block text-xs font-medium text-gray-700 mb-1"
								>
									{f.label}
								</label>
								{f.options ? (
									<select
										id={inputId}
										value={String(form[f.key] ?? "")}
										onChange={(e) => onChange(f.key, e.target.value)}
										disabled={!canEdit}
										className={fieldClassName}
									>
										<option value="" disabled>
											Select…
										</option>
										{f.options.map((o) => (
											<option key={o} value={o}>
												{o}
											</option>
										))}
									</select>
								) : (
									<input
										id={inputId}
										type={f.type}
										value={String(form[f.key] ?? "")}
										onChange={(e) => onChange(f.key, e.target.value)}
										disabled={!canEdit}
										className={fieldClassName}
									/>
								)}
							</div>
						);
					})}
				</div>
				<div className="flex gap-2 pt-1">
					{canEdit && (
						<>
							<button
								type="button"
								onClick={onSave}
								disabled={isSaving}
								className="flex-1 px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
							>
								{isSaving ? "Saving…" : "Save"}
							</button>
							{form.id !== undefined && (
								<button
									type="button"
									onClick={onDelete}
									disabled={isDeleting}
									className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
								>
									{isDeleting ? "…" : "Delete"}
								</button>
							)}
						</>
					)}
					<button
						type="button"
						onClick={onClose}
						className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
					>
						Cancel
					</button>
				</div>
			</div>
		</Modal>
	);
}

type MappingField<T> = {
	key: keyof T & string;
	label: string;
	type: "number" | "text";
	options?: readonly string[];
};

const fieldClassName =
	"w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-400";

function groupByYear<T extends { year?: number | null }>(
	items: T[],
): Map<number, T[]> {
	const map = new Map<number, T[]>();
	for (const item of items) {
		const y = item.year ?? 0;
		const group = map.get(y) ?? [];
		group.push(item);
		map.set(y, group);
	}
	return new Map([...map.entries()].sort((a, b) => b[0] - a[0]));
}

function groupByTowerName(
	items: SmartField_Api_Contracts_Mapping_TowerMappingDto[],
): Map<string, SmartField_Api_Contracts_Mapping_TowerMappingDto[]> {
	const map = new Map<string, SmartField_Api_Contracts_Mapping_TowerMappingDto[]>();
	for (const item of items) {
		const name = item.towerName ?? "";
		const group = map.get(name) ?? [];
		group.push(item);
		map.set(name, group);
	}
	return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
}

export function MappingPanel({
	siteId,
	year,
	canEdit,
	els,
	sensors,
	onBulkApplyChamberHeights,
	onBulkApplyTowerHeights,
	isBulkApplying,
}: Props) {
	const queryClient = useQueryClient();
	const [chambersOpen, setChambersOpen] = useState(false);
	const [towersOpen, setTowersOpen] = useState(false);
	const [editingChamber, setEditingChamber] = useState<EditingChamber | null>(
		null,
	);
	const [editingTower, setEditingTower] = useState<EditingTower | null>(null);
	const [configuringChamber, setConfiguringChamber] =
		useState<SmartField_Api_Contracts_Mapping_ChamberMappingDto | null>(null);
	const [configuringTowerGroup, setConfiguringTowerGroup] =
		useState<string | null>(null);

	const { data: chambers = [] } = useQuery({
		queryKey: ["chamber-mappings", siteId, year],
		queryFn: () => chamberMappingGetChamberMappings({ siteId, year }),
		enabled: !Number.isNaN(siteId) && !Number.isNaN(year),
	});

	const { data: towers = [] } = useQuery({
		queryKey: ["tower-mappings", siteId, year],
		queryFn: () => towerMappingGetTowerMappings({ siteId, year }),
		enabled: !Number.isNaN(siteId) && !Number.isNaN(year),
	});

	const saveChamberMutation = useMutation({
		mutationFn: async (form: EditingChamber) => {
			const year = parseStrictInt(form.year);
			const chamberNumber = parseStrictInt(form.chamberNumber);
			const trialId = parseStrictInt(form.trialId);
			const plotNumber = parseStrictInt(form.plotNumber);
			if (Number.isNaN(year) || Number.isNaN(chamberNumber) || Number.isNaN(trialId) || Number.isNaN(plotNumber)) {
				throw new Error('All fields must be valid numbers');
			}
			const payload = { year, chamberNumber, trialId, plotNumber };
			if (form.id != null) {
				await chamberMappingUpdateChamberMapping({
					siteId,
					id: form.id,
					requestBody: payload,
				});
			} else {
				await chamberMappingCreateChamberMapping({
					siteId,
					requestBody: payload,
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chamber-mappings", siteId] });
			setEditingChamber(null);
			toast.success("Chamber mapping saved");
		},
		onError: () => toast.error("Failed to save chamber mapping"),
	});

	const deleteChamberMutation = useMutation({
		mutationFn: (id: number) =>
			chamberMappingDeleteChamberMapping({ siteId, id }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chamber-mappings", siteId] });
			setEditingChamber(null);
			setConfiguringChamber(null);
			toast.success("Deleted");
		},
		onError: () => toast.error("Failed to delete"),
	});

	const saveTowerMutation = useMutation({
		mutationFn: async (form: EditingTower) => {
			const year = parseStrictInt(form.year);
			const trialId = parseStrictInt(form.trialId);
			const blockId = parseStrictInt(form.blockId);
			if (Number.isNaN(year) || Number.isNaN(trialId) || Number.isNaN(blockId)) {
				throw new Error('All fields must be valid numbers');
			}
			const payload = { year, towerName: form.towerName, trialId, blockId };
			if (form.id != null) {
				await towerMappingUpdateTowerMapping({
					siteId,
					id: form.id,
					requestBody: payload,
				});
			} else {
				await towerMappingCreateTowerMapping({ siteId, requestBody: payload });
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tower-mappings", siteId] });
			setEditingTower(null);
			toast.success("Tower mapping saved");
		},
		onError: () => toast.error("Failed to save tower mapping"),
	});

	const deleteTowerMutation = useMutation({
		mutationFn: (id: number) => towerMappingDeleteTowerMapping({ siteId, id }),
		onSuccess: (_data, id) => {
			queryClient.invalidateQueries({ queryKey: ["tower-mappings", siteId] });
			setEditingTower(null);
			if (configuringTowerGroup != null) {
				const remaining = towers.filter((t) => t.towerName === configuringTowerGroup && t.id !== id);
				if (remaining.length === 0) {
					setConfiguringTowerGroup(null);
				}
			}
			toast.success("Deleted");
		},
		onError: () => toast.error("Failed to delete"),
	});

	const saveChamberConfigMappingMutation = useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: SmartField_Api_Contracts_Mapping_ChamberMappingUpdateDto;
		}) => chamberMappingUpdateChamberMapping({ siteId, id, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chamber-mappings", siteId] });
			toast.success("Chamber mapping saved");
		},
		onError: () => toast.error("Failed to save chamber mapping"),
	});

	const saveTowerConfigMappingMutation = useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto;
		}) => towerMappingUpdateTowerMapping({ siteId, id, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tower-mappings", siteId] });
			toast.success("Tower mapping saved");
		},
		onError: () => toast.error("Failed to save tower mapping"),
	});

	const addTowerRowMutation = useMutation({
		mutationFn: (data: SmartField_Api_Contracts_Mapping_TowerMappingUpdateDto) =>
			towerMappingCreateTowerMapping({ siteId, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tower-mappings", siteId] });
			toast.success("Row added");
		},
		onError: () => toast.error("Failed to add row"),
	});

	const chamberFields: MappingField<EditingChamber>[] = [
		{ key: "year", label: "Year", type: "number" },
		{ key: "chamberNumber", label: "Chamber #", type: "number" },
		{ key: "trialId", label: "Trial ID", type: "number" },
		{ key: "plotNumber", label: "Plot #", type: "number" },
	];

	const towerFields: MappingField<EditingTower>[] = [
		{ key: "year", label: "Year", type: "number" },
		{ key: "towerName", label: "Tower Name", type: "text", options: TOWER_NAMES },
		{ key: "trialId", label: "Trial ID", type: "number" },
		{ key: "blockId", label: "Block #", type: "number" },
	];

	const chambersByYear = groupByYear(chambers);
	const towersByName = groupByTowerName(towers);

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4 text-sm">
			{/* Chamber Mappings */}
			<div>
				<SectionToggle
					label="Chamber Mappings"
					open={chambersOpen}
					onToggle={() => setChambersOpen((v) => !v)}
					count={chambers.length}
				/>
				{chambersOpen && (
					<div className="mt-2 space-y-3">
						{chambersByYear.size === 0 && (
							<p className="text-xs text-gray-400 px-1">No chamber mappings</p>
						)}
						{Array.from(chambersByYear.entries()).map(
							([year, yearChambers]) => (
								<div key={year}>
									<div className="flex items-center justify-between mb-1">
										<span className="text-xs font-semibold text-gray-500 px-1">
											{year}
										</span>
										{canEdit && (
											<button
												type="button"
												onClick={() =>
													setEditingChamber(emptyEditingChamber(String(year)))
												}
												className="text-xs px-1.5 py-0.5 text-smartfield hover:bg-blue-50 rounded"
											>
												+ Add
											</button>
										)}
									</div>
									<table className="w-full text-xs border-collapse">
										<thead>
											<tr className="border-b border-gray-200">
												<th className="text-left px-2 py-1 text-gray-400 font-medium">
													#
												</th>
												<th className="text-left px-2 py-1 text-gray-400 font-medium">
													Trial
												</th>
												<th className="text-left px-2 py-1 text-gray-400 font-medium">
													Plot
												</th>
												<th className="px-1 py-1 w-5" />
											</tr>
										</thead>
										<tbody>
											{yearChambers.map((c) => (
												<tr
													key={c.id}
													className="border-b border-gray-100 hover:bg-gray-50"
												>
													<td className="px-2 py-1">{c.chamberNumber}</td>
													<td className="px-2 py-1">{c.trialId}</td>
													<td className="px-2 py-1">{c.plotNumber}</td>
													<td className="px-1 py-1">
														<button
															type="button"
															onClick={() => setConfiguringChamber(c)}
															className="p-0.5 text-gray-400 hover:text-smartfield"
															title="Edit"
														>
															<EditIcon />
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							),
						)}
						{canEdit && chambersByYear.size === 0 && (
							<button
								type="button"
								onClick={() =>
									setEditingChamber(emptyEditingChamber(String(year)))
								}
								className="w-full px-3 py-1.5 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100"
							>
								+ Add chamber mapping
							</button>
						)}
					</div>
				)}
			</div>

			{/* Tower Mappings */}
			<div>
				<SectionToggle
					label="Tower Mappings"
					open={towersOpen}
					onToggle={() => setTowersOpen((v) => !v)}
					count={towers.length}
				/>
				{towersOpen && (
					<div className="mt-2 space-y-3">
						{towersByName.size === 0 && (
							<p className="text-xs text-gray-400 px-1">No tower mappings</p>
						)}
						<div className="grid grid-cols-1 gap-2">
							{Array.from(towersByName.entries()).map(([towerName, groupRows]) => (
								<button
									key={towerName}
									type="button"
									onClick={() => setConfiguringTowerGroup(towerName)}
									className="text-left border border-gray-200 rounded p-2 hover:border-smartfield hover:bg-blue-50/30"
								>
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-gray-900">{towerName}</span>
										<EditIcon />
									</div>
									<table className="w-full text-xs border-collapse mt-1">
										<thead>
											<tr className="border-b border-gray-200">
												<th className="text-left px-2 py-1 text-gray-400 font-medium">Trial</th>
												<th className="text-left px-2 py-1 text-gray-400 font-medium">Block</th>
												<th className="text-left px-2 py-1 text-gray-400 font-medium">Plots</th>
											</tr>
										</thead>
										<tbody>
											{groupRows.map((row) => (
												<tr key={row.id} className="border-b border-gray-100">
													<td className="px-2 py-1">{row.trialId}</td>
													<td className="px-2 py-1">
														{row.blockId != null ? (
															row.blockId
														) : (
															<span className="text-gray-400 italic">Unmapped</span>
														)}
													</td>
													<td className="px-2 py-1 text-gray-500">
														{row.plotIds && row.plotIds.length > 0 ? row.plotIds.join(", ") : "—"}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</button>
							))}
						</div>
						{canEdit && (
							<button
								type="button"
								onClick={() => setEditingTower(emptyEditingTower(String(year)))}
								className="w-full px-3 py-1.5 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100"
							>
								+ Add tower
							</button>
						)}
					</div>
				)}
			</div>

			{/* Chamber edit modal */}
			<MappingEditModal
				open={editingChamber != null}
				onClose={() => setEditingChamber(null)}
				title={
					editingChamber?.id != null
						? "Edit Chamber Mapping"
						: "Add Chamber Mapping"
				}
				form={editingChamber}
				fields={chamberFields}
				onChange={(key, value) =>
					setEditingChamber((prev) => (prev ? { ...prev, [key]: value } : prev))
				}
				onSave={() =>
					editingChamber && saveChamberMutation.mutate(editingChamber)
				}
				onDelete={() =>
					editingChamber?.id != null &&
					deleteChamberMutation.mutate(editingChamber.id)
				}
				isSaving={saveChamberMutation.isPending}
				isDeleting={deleteChamberMutation.isPending}
				canEdit={canEdit}
			/>

			{/* Tower edit modal */}
			<MappingEditModal
				open={editingTower != null}
				onClose={() => setEditingTower(null)}
				title={
					editingTower?.id != null ? "Edit Tower Mapping" : "Add Tower Mapping"
				}
				form={editingTower}
				fields={towerFields}
				onChange={(key, value) =>
					setEditingTower((prev) => (prev ? { ...prev, [key]: value } : prev))
				}
				onSave={() => editingTower && saveTowerMutation.mutate(editingTower)}
				onDelete={() =>
					editingTower?.id != null &&
					deleteTowerMutation.mutate(editingTower.id)
				}
				isSaving={saveTowerMutation.isPending}
				isDeleting={deleteTowerMutation.isPending}
				canEdit={canEdit}
			/>

			<Modal open={configuringChamber != null} onClose={() => setConfiguringChamber(null)} size="lg">
				{configuringChamber && (
					<ChamberConfigModal
						mapping={configuringChamber}
						els={els}
						sensors={sensors}
						onSaveMapping={(data) =>
							saveChamberConfigMappingMutation.mutate({ id: configuringChamber.id!, data })
						}
						onSaveHeights={(elId, effectiveFrom, effectiveTo, data) =>
							onBulkApplyChamberHeights(elId, effectiveFrom, effectiveTo, data)
						}
						onDelete={() =>
							configuringChamber?.id != null &&
							deleteChamberMutation.mutate(configuringChamber.id)
						}
						onCancel={() => setConfiguringChamber(null)}
						isSaving={saveChamberConfigMappingMutation.isPending || isBulkApplying}
						isDeleting={deleteChamberMutation.isPending}
						canEdit={canEdit}
					/>
				)}
			</Modal>

			<Modal open={configuringTowerGroup != null} onClose={() => setConfiguringTowerGroup(null)} size="lg">
				{configuringTowerGroup && (
					<TowerGroupConfigModal
						towerName={configuringTowerGroup}
						rows={towers.filter((t) => t.towerName === configuringTowerGroup)}
						els={els}
						sensors={sensors}
						onSaveRow={(id, data) => saveTowerConfigMappingMutation.mutate({ id, data })}
						onAddRow={(data) => addTowerRowMutation.mutate(data)}
						onSaveHeightsGroup={(sensorIds, effectiveFrom, effectiveTo, data) =>
							onBulkApplyTowerHeights(sensorIds, effectiveFrom, effectiveTo, data)
						}
						onCancel={() => setConfiguringTowerGroup(null)}
						isSaving={saveTowerConfigMappingMutation.isPending || addTowerRowMutation.isPending || isBulkApplying}
						canEdit={canEdit}
					/>
				)}
			</Modal>
		</div>
	);
}
